<template>
    <div :ref="id" :id="id" class="h-100"></div>
</template>

<script>
    import * as d3 from "d3";
    import d3Tip from "d3-tip";

    export default {
        name: "BarChart",
        props: {
            id: String,
            data: Array,
            valueAttr: String,
            labelAttr: String
        },
        data() {
            return {
                chart: {
                    data: [],
                }
            };
        },
        mounted() {
            this.prepareChart();
        },
        methods: {
            prepareChart: function () {
                if(!this.data) {
                    return;
                }
                var margin = {top: 35, right: 30, bottom: 50, left: 50},
                    width = this.$refs[this.id].clientWidth - margin.left - margin.right,
                    height = this.$refs[this.id].clientHeight - margin.top - margin.bottom;
                this.chart.barsNumber = width / 50;
                this.chart.width = width;
                this.chart.height = height;
                this.chart.margin = margin;

                // Clean the svg
                d3.select("#svg-" + this.id).remove();
                d3.selectAll('.bar-chart-tooltip').remove();

                var svg = d3
                    .select('#' + this.id)
                    .append("svg")
                    .attr("id", "svg-" + this.id)
                    .attr("width", width + margin.left + margin.right)
                    .attr("height", height + margin.top + margin.bottom)
                    .append("g")
                        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

                this.chart.svg = svg;
                var linearGradient = svg.append("defs")
                    .append("linearGradient")
                    .attr("id", "linear-gradient");

                linearGradient.append("stop")
                    .attr("offset", "0%")
                    .attr("stop-color", "#2e7d32");
                linearGradient.append("stop")
                    .attr("offset", "50%")
                    .attr("stop-color", "#43a047");
                linearGradient.append("stop")
                    .attr("offset", "100%")
                    .attr("stop-color", "#2e7d32");


                const maxValue = d3.max(this.data, (d) => d[this.valueAttr]);
                const minValue = d3.min(this.data, (d) => d[this.valueAttr]);

                const nonNegativeAxis = minValue >= 0 && maxValue >= 0;
                const positiveAndNegativeAxis = minValue < 0 && maxValue > 0;

                let yScaleDomain, xAxisPosition;

                if(nonNegativeAxis) {
                    yScaleDomain = [0, d3.max(this.data, (d) => d[this.valueAttr])];
                }  else if(positiveAndNegativeAxis) {
                    yScaleDomain = d3.extent(this.data, (d) => d[this.valueAttr]);
                } else {
                    yScaleDomain = [d3.min(this.data, (d) => d[this.valueAttr]), 0];
                }

                const xScale = d3.scaleBand()
                    .range([0, width])
                    .padding(0.4)
                    .domain(this.data.map((d) => d[this.labelAttr]));

                const yScale = d3.scaleLinear()
                    .range([height, 0])
                    .domain(yScaleDomain);

                if(nonNegativeAxis) {
                    xAxisPosition = height;
                } else if(positiveAndNegativeAxis) {
                    xAxisPosition = yScale(0);
                } else {
                    xAxisPosition = 0;
                }

                svg
                    .append("g")
                    .attr("id", this.id +"-labels")
                    .attr("transform", "translate(0,"+ (xAxisPosition) + ")")      // This controls the rotate position of the Axis
                    .call(d3.axisBottom(xScale))
                    .selectAll("text")
                    .attr("transform", function(d, i){
                        const mod2 = i % 2;
                        const multiple = this.data[i].percent < 0 ? -1 : 1;
                        let y = this.data[i].percent < 0 ? - 30 : 0;
                        y += multiple * mod2 * 15;
                        return "translate(0," + y + ")"; }.bind(this))
                    .style("font-weight", 200)
                    .style("font-size", 12);

                d3.selectAll(`#${ this.id}-labels .tick line`)
                    .attr("y2", (d, i) => {
                        return this.data[i] && this.data[i].percent < 0 ? -6 : 6;
                    });

                var tooltip = d3Tip()
                    .attr('class', 'chart-tooltip')
                    .offset([-10, 0])
                    .html(d =>
                        d.tooltip
                    );
                svg.call(tooltip);

                svg.append("g")
                    .selectAll("rect")
                    .data(this.data)
                    .enter().append("rect")
                    .attr("class", "bar")
                    .attr("x", (d) => xScale(d[this.labelAttr]))
                    .attr("y", (d) => d[this.valueAttr] < 0 ? xAxisPosition : yScale(d[this.valueAttr]))
                    .attr("height", (d) => Math.abs(xAxisPosition - yScale(d[this.valueAttr])))
                    .attr("width", xScale.bandwidth())
                    .style("fill", "url(#linear-gradient)")
                    .on('mouseover', tooltip.show)
                        .on('mouseout', tooltip.hide);

                svg.append("g")
                    .attr("class", "chart-axis-label")
                    .call(d3.axisLeft(yScale).tickFormat(function (d) {
                        return d + "%";
                    }).ticks(3))
                    .append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", 6)
                    .attr("dy", "-5.1em")
                    .attr("text-anchor", "end")
                    .attr("stroke", "black")
            }
        },
        watch: {
            "data": function () {
                this.prepareChart();
            }
        }
    }
</script>
